import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  RotateCcw,
  ArrowRight,
  Lightbulb,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Calendar,
  TrendingUp,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RoleResultCard from "../components/RoleResultCard";

const CALENDLY_URL =
  "https://calendly.com/anantmattoo/free-30-minute-discovery-call";

const DEMAND_BADGE = {
  "Very High": "bg-emerald-100 text-emerald-800 border-emerald-200",
  High:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  Moderate:    "bg-amber-50 text-amber-800 border-amber-200",
  Low:         "bg-slate-100 text-slate-700 border-slate-200",
};

const TIER_BADGE = {
  Primary:               "bg-emerald-500 text-white",
  Secondary:             "bg-sky-500 text-white",
  Stretch:               "bg-amber-500 text-white",
  "Closest contender":   "bg-slate-600 text-white",
};

// Render simple **bold** markers in the verdict paragraph as actual <strong> tags.
const renderInline = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    // Keys combine index + content so React can still distinguish duplicate
    // segments (e.g. two plain ". " separators) without warnings.
    const key = `${i}-${p.slice(0, 12)}`;
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return (
        <strong key={key} className="font-bold text-slate-900">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={key}>{p}</span>;
  });
};

const StickyScore = ({ score, role, tier }) => (
  <div
    data-testid="sticky-score"
    className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
  >
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
        Skills Match
      </p>
      <p className="font-display text-lg font-black text-slate-900 truncate">
        {score}/100 · {role}
      </p>
    </div>
    <span
      className={`shrink-0 ml-3 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${TIER_BADGE[tier] || "bg-slate-200"}`}
    >
      {tier}
    </span>
  </div>
);

const Results = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("rolefinder_result");
    const unlocked = sessionStorage.getItem("rolefinder_unlocked");
    if (!raw) {
      navigate("/assessment");
      return;
    }
    if (!unlocked) {
      navigate("/unlock");
      return;
    }
    try {
      setResult(JSON.parse(raw).result);
    } catch {
      navigate("/assessment");
    }
  }, [navigate]);

  // Top 3 skills to learn next — now sourced from the backend, which pulls
  // gaps only from Primary + Secondary roles (not Stretch) so coaching
  // recommendations stay relevant to the roles the candidate is actually
  // aiming for.
  const topMissing = result?.skills_to_learn_next || [];
  const noGapsMessage = result?.skills_to_learn_next_message || null;

  if (!result) return null;

  const coRoles = result.co_primary_roles || [];
  const hero = coRoles[0] || result.primary || result.top_overall;
  if (!hero) return null;

  const isCoPrimary = result.co_primary && coRoles.length >= 2;
  const partner = isCoPrimary ? coRoles[1] : null;

  const heroScore = Math.round(hero.skills_match ?? hero.final_score ?? 0);
  const partnerScore = partner ? Math.round(partner.skills_match) : null;
  const tier = hero.recommendation;
  const demandLevel = hero?.components?.demand_level || "Moderate";
  const nextSkill = result.mailerlite?.next_skill_to_add;

  // 3-branch congratulatory headline (post-email reveal).
  // Priority:
  //   1. Co-primary  → "equally strong match for {a} and {b}"
  //   2. One-skill-away → leader is NOT a clean Primary (or has a
  //      mandatory gap) AND we know what skill to add
  //   3. Strong match → default for clean Primary fits
  const demandWord = demandLevel.toLowerCase();
  const isCleanPrimary =
    tier === "Primary" && (hero.missing_mandatory?.length || 0) === 0;
  let congrats;
  if (isCoPrimary && partner && nextSkill) {
    congrats = {
      kind: "co_primary",
      text: (
        <>
          <span aria-hidden="true">🎉</span> You&rsquo;re an equally strong
          match for <b>{hero.role_name}</b> and <b>{partner.role_name}</b>.{" "}
          The one skill that tips it: <b>{nextSkill}</b>.
        </>
      ),
    };
  } else if (!isCleanPrimary && nextSkill) {
    congrats = {
      kind: "one_skill_away",
      text: (
        <>
          <span aria-hidden="true">🎉</span> You&rsquo;re just one skill away
          from <b>{hero.role_name}</b> &mdash; add <b>{nextSkill}</b> and
          you&rsquo;re there. And it&rsquo;s in <b>{demandWord}</b> demand.
        </>
      ),
    };
  } else {
    congrats = {
      kind: "strong_match",
      text: (
        <>
          <span aria-hidden="true">🎉</span> Congratulations &mdash; your
          best-fit role is <b>{hero.role_name}</b>, and it&rsquo;s in{" "}
          <b>{demandWord}</b> demand. You&rsquo;re a strong match.
        </>
      ),
    };
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 lg:pb-0">
      <Header />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Headline + retake */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
              Your personalised Skills Match
            </p>
            <h1
              data-testid="results-title"
              className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900"
            >
              {isCoPrimary
                ? "You're a strong match for two roles."
                : "Here's the role you should aim for."}
            </h1>
          </div>
          <Link
            to="/assessment"
            data-testid="retake-link"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RotateCcw size={14} /> Retake
          </Link>
        </div>

        {/* HERO: Skills Match + role + tier badge */}
        <section
          data-testid="skills-match-hero"
          className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/40 p-8 lg:p-10 animate-fade-up"
        >
          <div
            data-testid={`congrats-${congrats.kind}`}
            className="rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-4 mb-6 text-emerald-900 text-base sm:text-lg leading-relaxed font-medium"
          >
            {congrats.text}
          </div>

          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex-1 min-w-[260px]">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700">
                Skills Match
              </p>
              <div className="mt-1 flex items-baseline gap-3">
                <span
                  data-testid="skills-match-score"
                  className="font-display font-black text-6xl lg:text-7xl text-slate-900 leading-none"
                >
                  {heroScore}
                </span>
                <span className="font-display text-2xl text-slate-400 font-bold">
                  /100
                </span>
              </div>
              <h2
                data-testid="hero-role-name"
                className="mt-4 font-display text-2xl lg:text-3xl font-black text-slate-900"
              >
                {hero.role_name}
                {partner && (
                  <>
                    {" "}
                    <span className="font-normal text-slate-400 text-xl">
                      &amp;
                    </span>{" "}
                    <span data-testid="hero-partner-role">{partner.role_name}</span>{" "}
                    <span className="text-slate-500 text-xl font-bold">
                      ({partnerScore}/100)
                    </span>
                  </>
                )}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {hero.role_category}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span
                  data-testid="tier-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${TIER_BADGE[tier] || "bg-slate-200"}`}
                >
                  <Sparkles size={12} />
                  {tier}
                </span>
                <span
                  data-testid="demand-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${DEMAND_BADGE[demandLevel] || DEMAND_BADGE.Moderate}`}
                >
                  <TrendingUp size={12} />
                  {demandLevel} demand
                </span>
              </div>
            </div>

            {/* Score arc / mini gauge */}
            <div
              data-testid="score-gauge"
              className="relative w-32 h-32 shrink-0 hidden sm:block"
            >
              <svg viewBox="0 0 120 120" className="w-full h-full">
                <circle cx="60" cy="60" r="52" stroke="#E2E8F0" strokeWidth="10" fill="none" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  stroke="#F59E0B"
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(heroScore / 100) * 326.7} 326.7`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div
                data-testid="score-gauge-label"
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <span className="font-display font-black text-2xl text-slate-900">
                  {heroScore}
                  <span className="text-slate-500 font-bold">%</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Plain-English coach verdict */}
        <section
          data-testid="verdict-paragraph"
          className="mt-6 rounded-xl border border-slate-200 bg-white p-6 lg:p-8 animate-fade-up delay-1"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
            Coach&rsquo;s take
          </p>
          <p className="mt-2 text-slate-800 text-base lg:text-lg leading-relaxed">
            {renderInline(result.verdict)}
          </p>
          {result.differentiators &&
            (result.differentiators.to_lock_in_a?.length ||
              result.differentiators.to_pivot_to_b?.length) && (
              <div
                data-testid="differentiators"
                className="mt-4 rounded-md bg-amber-50/70 border border-amber-200 p-3 text-[13px] text-amber-900"
              >
                <p className="font-semibold mb-1">What separates them</p>
                <ul className="space-y-1">
                  {result.differentiators.to_lock_in_a?.length > 0 && (
                    <li>
                      To lock in <b>{hero.role_name}</b>:{" "}
                      {result.differentiators.to_lock_in_a.join(", ")}
                    </li>
                  )}
                  {partner && result.differentiators.to_pivot_to_b?.length > 0 && (
                    <li>
                      To pivot to <b>{partner.role_name}</b>:{" "}
                      {result.differentiators.to_pivot_to_b.join(", ")}
                    </li>
                  )}
                </ul>
              </div>
            )}
        </section>

        {/* Market Readiness — context only */}
        {result.market_readiness && (
          <section
            data-testid="market-readiness"
            className="mt-6 rounded-xl border border-slate-200 bg-white animate-fade-up delay-2"
          >
            <button
              type="button"
              data-testid="market-readiness-toggle"
              onClick={() => setReadinessOpen((v) => !v)}
              className="w-full flex items-center justify-between p-5 lg:p-6 text-left hover:bg-slate-50 rounded-xl"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
                  Market readiness
                </p>
                <p className="mt-1 text-[13px] text-slate-600">
                  <span className="font-semibold text-slate-800">
                    Context, not part of your score.
                  </span>{" "}
                  Experience, Australian-market readiness and demand for this role.
                </p>
              </div>
              {readinessOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {readinessOpen && (
              <div className="px-5 lg:px-6 pb-6 grid sm:grid-cols-3 gap-3">
                <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Experience
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {result.market_readiness.experience_label}
                  </p>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Australian readiness
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {result.market_readiness.local_band_label}
                  </p>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Role demand
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {result.market_readiness.demand_level} for {hero.role_name}
                  </p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Quickest-wins + strengthen */}
        {(hero.missing_mandatory?.length > 0 || hero.missing_important?.length > 0) && (
          <section className="mt-6 grid lg:grid-cols-2 gap-4 animate-fade-up delay-3">
            {hero.missing_mandatory?.length > 0 && (
              <div
                data-testid="quickest-wins"
                className="rounded-xl border border-rose-200 bg-rose-50/60 p-5"
              >
                <p className="flex items-center gap-2 text-sm font-bold text-rose-800">
                  <Lightbulb size={16} />
                  Your quickest wins to unlock this role
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-rose-900">
                  {hero.missing_mandatory.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="inline-block mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-500" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {hero.missing_important?.length > 0 && (
              <div
                data-testid="strengthen-skills"
                className="rounded-xl border border-amber-200 bg-amber-50/60 p-5"
              >
                <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                  <Sparkles size={16} />
                  Skills that strengthen your application
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-amber-900">
                  {hero.missing_important.slice(0, 6).map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="inline-block mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Secondary + stretch (collapsible "more options") */}
        <section className="mt-8">
          <button
            type="button"
            data-testid="details-toggle"
            onClick={() => setDetailsOpen((v) => !v)}
            className="w-full inline-flex items-center justify-between rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            <span>
              {detailsOpen ? "Hide" : "Show"} other roles &amp; tech/soft breakdown
            </span>
            {detailsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {detailsOpen && (
            <div className="mt-5 space-y-8">
              {/* Tech/Soft bars on the hero */}
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <RoleResultCard role={hero} isBestFit />
              </div>

              {/* Top 3 skills to learn next (from Primary + Secondary gaps) */}
              {(topMissing.length > 0 || noGapsMessage) && (
                <div
                  data-testid="skills-to-learn-next"
                  className="rounded-xl border border-slate-200 bg-white p-5"
                >
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
                    Across your top recommendations
                  </p>
                  <h3 className="mt-1 font-display text-xl font-bold text-slate-900">
                    {topMissing.length > 0
                      ? `Top ${topMissing.length} skills to learn next`
                      : "Top skills to learn next"}
                  </h3>
                  {topMissing.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {topMissing.map((s) => (
                        <span
                          key={s}
                          className="inline-flex rounded-full border border-amber-300 bg-amber-50 text-amber-900 text-sm font-medium px-3 py-1.5"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p
                      data-testid="skills-to-learn-next-empty"
                      className="mt-3 text-sm text-slate-700"
                    >
                      {noGapsMessage}
                    </p>
                  )}
                </div>
              )}

              {result.secondary && result.secondary.role_id !== hero.role_id && (
                <div data-testid="secondary-block">
                  <h3 className="font-display text-lg font-bold text-slate-900 mb-3">
                    Solid backup
                  </h3>
                  <RoleResultCard role={result.secondary} />
                </div>
              )}

              {result.stretch?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900 mb-3">
                    Stretch — realistic next-step targets
                  </h3>
                  <div className="grid gap-4 lg:grid-cols-2">
                    {result.stretch.map((r) => (
                      <RoleResultCard key={r.role_id} role={r} />
                    ))}
                  </div>
                </div>
              )}

              {result.not_recommended?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">
                    Bigger stretch — roles to aim for later
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Bookmark these for after you close a few skill gaps.
                  </p>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                    {result.not_recommended.map((r) => (
                      <li
                        key={r.role_id}
                        data-testid={`not-recommended-${r.role_id}`}
                        className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm flex items-center justify-between"
                      >
                        <span className="font-medium text-slate-800 truncate">
                          {r.role_name}
                        </span>
                        <span className="font-mono text-xs text-slate-500">
                          {Math.round(r.skills_match ?? r.final_score)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        {/* CTA — Calendly */}
        <section
          data-testid="results-cta"
          className="mt-12 rounded-2xl border border-slate-200 bg-slate-900 text-white p-8 lg:p-12 grid lg:grid-cols-3 gap-6 items-center animate-fade-up"
        >
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-400">
              Coaching support
            </p>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-black">
              Want help closing the skill gap that gets you there?
            </h3>
            <p className="mt-2 text-slate-300 max-w-xl">
              In a free 30-minute discovery call, Anant will walk through your
              {" "}{hero.role_name} fit, the ATS-friendly CV tweaks that work in
              Australia, and the one skill to prioritise next.
            </p>
          </div>
          <div className="lg:text-right">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="book-call-btn"
              className="inline-flex items-center gap-2 rounded-md bg-amber-500 text-slate-900 px-5 py-3 text-sm font-bold hover:bg-amber-400 transition-colors"
            >
              <Calendar size={16} />
              Book a free discovery call
              <ArrowRight size={16} />
            </a>
          </div>
        </section>
      </main>

      {/* Mobile sticky score bar */}
      <StickyScore score={heroScore} role={hero.role_name} tier={tier} />

      <Footer />
    </div>
  );
};

export default Results;
